import { APP_NAME } from '@/branding';
import { useState } from 'react';
import { Form, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { config } from '@/config';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { SignedInDevices } from '@/components/account/SignedInDevices';
import { SecurityKeys } from '@/components/account/SecurityKeys';
import { LinkedAccounts } from '@/features/oauthSso';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ChangePasswordFormInputs {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Password, and whatever else comes to guard sign-in later.
 *
 * Behavior preserved from the former Profile page, including the external-SSO case:
 * an account with no local password skips the current-password requirement rather
 * than blocking someone out of setting one.
 */
export default function SecuritySettings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const {
    register: registerPassword,
    control: controlPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<ChangePasswordFormInputs>();

  const changePasswordMutation = useMutation<
    undefined,
    AxiosError<{ error: string; description?: string }>,
    ChangePasswordFormInputs
  >({
    mutationFn: async (data) => {
      await axios.put(`${config.backendApi}/api/users/me/password`, {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
    },
    onSuccess: () => {
      resetPassword();
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
  });

  const handleCancelPassword = () => {
    resetPassword();
    changePasswordMutation.reset();
    setIsChangingPassword(false);
  };

  if (!user) return null;

  return (
    <PageContainer width="medium">
      <PageHeader title="Security" subtitle={`How you sign in to ${APP_NAME}`} />

      <section className="bg-card border border-border rounded-lg">
        <header className="px-5 py-4 border-b border-border flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Change password</h2>
          {!isChangingPassword && !changePasswordMutation.isSuccess ? (
            <Button variant="outline" size="sm" onClick={() => { setIsChangingPassword(true); }}>
              Change password
            </Button>
          ) : null}
        </header>

        {isChangingPassword && !changePasswordMutation.isSuccess ? (
          <Form
            control={controlPassword}
            onSubmit={({ data }) => { changePasswordMutation.mutate(data); }}
            className="p-5 space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="profile-currentPassword">Current password</Label>
              <Input
                id="profile-currentPassword"
                type="password"
                autoComplete="current-password"
                aria-invalid={!!passwordErrors.currentPassword}
                disabled={!user.hasPassword}
                {...registerPassword('currentPassword', {
                  required: user.hasPassword ? 'Current password is required' : false,
                })}
              />
              {!user.hasPassword ? (
                <p className="text-xs text-muted-foreground">
                  Not required — your account uses external sign-in.
                </p>
              ) : null}
              {passwordErrors.currentPassword ? (
                <p className="text-xs text-destructive">{passwordErrors.currentPassword.message}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="profile-newPassword">New password</Label>
              <Input
                id="profile-newPassword"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!passwordErrors.newPassword}
                {...registerPassword('newPassword', { required: 'New password is required' })}
              />
              {passwordErrors.newPassword ? (
                <p className="text-xs text-destructive">{passwordErrors.newPassword.message}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="profile-confirmPassword">Confirm new password</Label>
              <Input
                id="profile-confirmPassword"
                type="password"
                autoComplete="new-password"
                aria-invalid={!!passwordErrors.confirmPassword}
                {...registerPassword('confirmPassword', {
                  required: 'Please confirm your new password',
                  validate: (val: string, values) => values.newPassword === val || 'Passwords do not match',
                })}
              />
              {passwordErrors.confirmPassword ? (
                <p className="text-xs text-destructive">{passwordErrors.confirmPassword.message}</p>
              ) : null}
            </div>

            {changePasswordMutation.isError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Error changing password:</strong>{' '}
                  {changePasswordMutation.error.response?.data.description ??
                    changePasswordMutation.error.response?.data.error ??
                    'Failed to change password'}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelPassword}
                disabled={changePasswordMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={changePasswordMutation.isPending}>
                {changePasswordMutation.isPending ? 'Updating…' : 'Update password'}
              </Button>
            </div>
          </Form>
        ) : null}

        {changePasswordMutation.isSuccess ? (
          <div className="p-5">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>Password changed successfully.</AlertDescription>
            </Alert>
            <div className="flex justify-end mt-3">
              <Button variant="outline" onClick={handleCancelPassword}>
                Done
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <div className="mt-4 md:mt-5">
        <SecurityKeys />

      <LinkedAccounts />

      <SignedInDevices />
      </div>
    </PageContainer>
  );
}
